import type { AxiosPromise } from "axios";
import { route } from "../../shim/jsbridge";

import { AxiosApiWrapper } from "../../services/api/AxiosApiWrapper";
import {
  DnsReport,
  DomainProp,
  DomainReport,
  DomainResolutionsResponse,
  DomainUrlsResponse,
  IPDomainResolutionsResponse,
  IPDownloadedFiles,
  IPReport,
  IPUrlsResponse,
  JoeURLSubmissionResponse,
  PayloadsReport,
  QueryParamProps,
  RelatedDomainsResponse,
  UrlReport,
  WhoIsReport
} from "./@types";
import { NetworkReputationReport, UrlDom } from "./@types/Url";
import { URLAnalysisStatus, UrlIsAnalyzing } from "./@types/analysis-status";
import { NetworkClassification } from "./@types/common";

type PaginationRequest = {
  page?: string;
  pageSize?: string | number;
};

type DownloadedFilesRequest = {
  extended?: boolean;
  classification?: NetworkClassification;
} & PaginationRequest;

class Api extends AxiosApiWrapper {
  urlReputation = (data: unknown): AxiosPromise<NetworkReputationReport> =>
    this.post(route("api-network-reputation"), data);
  urlReport = ({
    url,
    signal
  }: {
    url: string;
    signal?: AbortSignal;
  }): AxiosPromise<UrlReport> =>
    this.get(route("api-network-threat-intel-url", {}, { url }), { signal });

  domainReport = ({ domain }: { domain: string }): AxiosPromise<DomainReport> =>
    this.get(route("api-network-threat-intel-domain", [domain]));

  ipReport = ({ ip }: { ip: string }): AxiosPromise<IPReport> =>
    this.get(route("api-network-threat-intel-ip-report", { value: ip }));

  dnsReport = ({ url }: { url: string }): AxiosPromise<DnsReport> =>
    this.post(route("api-network-threat-intel-dns"), {
      url
    });

  whoisReport = ({ url }: { url: string }): AxiosPromise<WhoIsReport> =>
    this.post(route("api-network-threat-intel-whois-tool"), {
      uri: url
    });

  getUrlPayloads = ({
    url,
    queryParams
  }: {
    url: string;
    queryParams: QueryParamProps;
  }): AxiosPromise<PayloadsReport> =>
    this.post(route("api-sample-browse-url-payloads", [url], queryParams), {
      url
    });

  ipResolutions = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPDomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-resolutions",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainResolutions = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-resolutions",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainRelatedDomains = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<RelatedDomainsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-related-domains",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainUrls = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-urls",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  ipURLs = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-urls",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainDownloadedFiles = ({
    domain,
    page,
    pageSize,
    extended,
    classification
  }: DomainProp & DownloadedFilesRequest) =>
    this.get(
      route(
        "api-network-threat-intel-domain-downloaded-files",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      )
    );

  urlDownloadedFiles = ({
    url,
    page,
    pageSize,
    extended,
    classification
  }: {
    url: string;
  } & DownloadedFilesRequest) =>
    this.post(route("api-network-threat-intel-url-downloaded-files"), {
      url,
      ...(page && { page }),
      ...(pageSize && { page_size: pageSize }),
      ...(extended && { extended }),
      ...(classification && { classification: classification.toUpperCase() })
    });

  ipDownloadedFiles = ({
    ip,
    page,
    pageSize,
    extended,
    classification
  }: {
    ip: string;
  } & DownloadedFilesRequest): AxiosPromise<IPDownloadedFiles> =>
    this.get(
      route(
        "api-network-threat-intel-ip-downloaded-files",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended }),
          ...(classification && {
            classification: classification.toUpperCase()
          })
        }
      )
    );

  urlDynamicAnalysisStatus = (
    url: string,
    signal: AbortSignal
  ): AxiosPromise<URLAnalysisStatus> =>
    this.post(
      route("api_rl_dynamic_analysis_url_report_display_status"),
      {
        url
      },
      { signal }
    );

  getAnalysisStatus = (url: string, signal: AbortSignal) =>
    this.post<UrlIsAnalyzing>(
      route("url_analysis_status"),
      { url },
      { signal }
    );

  getDomPreviewAndLinks = ({
    url,
    domUrl
  }: {
    url: string;
    domUrl?: string;
  }): AxiosPromise<UrlDom> =>
    this.get(route("api-network-threat-intel-url-dom"), {
      params: {
        url,
        dom_url: domUrl
      }
    });

  getJoeUrlSubmission = (url: string): AxiosPromise<JoeURLSubmissionResponse> =>
    this.get(route("api_joe_url_submission", [], { url }));

  getUsableUrlScreenshot = ({ urls }: { urls?: Array<string> }) =>
    this.post(route("api-network-threat-intel-usable-url-screenshot"), {
      urls
    });

  postFalseReport = (
    url: string,
    type: string,
    comment: string,
    contact?: string
  ) =>
    this.post(route("api-network-threat-intel-false-report"), {
      url,
      type,
      comment,
      contact
    });
}
export default new Api();
